/**
 * Which questions the applicant sees, given their answers — a port of
 * `questionnaires::graph::{walk, condition_holds}`. The server re-walks on
 * submission and refuses answers off the path, so the two MUST agree:
 * `walk.test.ts` replays `backend/contracts/questionnaires/walk-vectors.json`,
 * which Rust generates.
 */
import type { Answer, Condition, Graph, Question } from './types';

type Answers = Record<string, Answer | null | undefined>;

const question = (graph: Graph, id: string): Question | null => {
  const n = graph.nodes[id];
  return n?.kind === 'question' ? n : null;
};

const scalar = (a: Answer | null | undefined): unknown =>
  a && 'value' in a ? a.value : undefined;

function equals(type: Question['element_type'], a: unknown, b: unknown): boolean {
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (typeof a === 'string' && typeof b === 'string') {
    return type === 'short_text' || type === 'long_text'
      ? a.trim().toLowerCase() === b.trim().toLowerCase()
      : a === b;
  }
  return false;
}

function contains(a: unknown, b: unknown): boolean {
  if (typeof b !== 'string') return false;
  if (Array.isArray(a)) return a.includes(b);
  if (typeof a === 'string') return a.toLowerCase().includes(b.toLowerCase());
  return false;
}

function compare(type: Question['element_type'], a: unknown, b: unknown): -1 | 0 | 1 | null {
  if (type === 'number') {
    if (typeof a !== 'number' || typeof b !== 'number') return null;
    return a < b ? -1 : a > b ? 1 : 0;
  }
  if (type === 'date_picker' || type === 'time') {
    if (typeof a !== 'string' || typeof b !== 'string') return null;
    return a < b ? -1 : a > b ? 1 : 0;
  }
  return null;
}

/** An unanswered question is "empty": it equals nothing and contains nothing. */
export function conditionHolds(graph: Graph, c: Condition, answers: Answers): boolean {
  const q = question(graph, c.field);
  if (!q) return false;
  const answer = answers[c.field];
  const empty = answer === null || answer === undefined;
  const a = scalar(answer);
  const b = c.value ?? undefined;
  switch (c.operator) {
    case 'is_empty':
      return empty;
    case 'is_not_empty':
      return !empty;
    case 'equals':
      return !empty && equals(q.element_type, a, b);
    case 'not_equals':
      return empty || !equals(q.element_type, a, b);
    case 'contains':
      return !empty && contains(a, b);
    case 'not_contains':
      return empty || !contains(a, b);
    case 'greater_than':
      return compare(q.element_type, a, b) === 1;
    case 'less_than':
      return compare(q.element_type, a, b) === -1;
  }
}

function branchTarget(graph: Graph, id: string, answers: Answers): string | null {
  const n = graph.nodes[id];
  if (n?.kind !== 'branching') return null;
  for (const b of n.branches) {
    const holds =
      b.logic === 'or'
        ? b.conditions.some((c) => conditionHolds(graph, c, answers))
        : b.conditions.every((c) => conditionHolds(graph, c, answers));
    if (holds && b.conditions.length > 0) return b.next ?? null;
  }
  return n.else_next ?? null;
}

/**
 * The applicant's path. With `waitOnUnanswered`, the walk stops at a
 * branching node that depends on a required question already shown but not
 * yet answered — so the form grows as answers come in instead of showing the
 * "else" path first and then swapping it out.
 */
export function walk(graph: Graph, answers: Answers, waitOnUnanswered = false): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let cur = graph.start_node ?? null;
  while (cur && !seen.has(cur)) {
    const node = graph.nodes[cur];
    if (!node) break;
    seen.add(cur);
    if (node.kind === 'branching' && waitOnUnanswered) {
      const pending = node.branches.some((b) =>
        b.conditions.some((c) => {
          const q = question(graph, c.field);
          return q?.required && seen.has(c.field) && (answers[c.field] ?? null) === null;
        }),
      );
      if (pending) break;
    }
    out.push(cur);
    cur = node.kind === 'branching' ? branchTarget(graph, cur, answers) : (node.next ?? null);
  }
  return out;
}
